/**
 * The transport layer. Generated request/response types will land in
 * ./generated and be re-exported here; this file stays hand-written.
 *
 * Two things live here that a generator will never produce correctly:
 * the Idempotency-Key on every write, and the error envelope.
 */

export interface ApiErrorBody {
    error: {
        code: string;
        message: string;
        details?: Record<string, string[]>;
    };
}

export class ApiError extends Error {
    constructor(
        readonly status: number,
        readonly code: string,
        message: string,
        readonly details?: Record<string, string[]>,
    ) {
        super(message);
        this.name = 'ApiError';
    }

    /** Validation failures are the only errors a form should render inline. */
    get isValidation(): boolean {
        return this.code === 'validation_failed';
    }
}

export interface ClientOptions {
    baseUrl: string;
    /** Returns the bearer token, or null when signed out. */
    token: () => string | null;
    /**
     * Returns the ops console's current data mode. Defaults to always-live
     * so a caller that never passes this (the merchant and rider surfaces
     * have no such concept) is unaffected.
     */
    dataMode?: () => 'live' | 'testing';
}

type Surface = 'ops' | 'merchant' | 'rider';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function createClient({ baseUrl, token, dataMode }: ClientOptions) {
    async function request<T>(
        // Null for the one door that has no surface yet: the unified login
        // endpoint, which is how a caller finds out its surface in the first
        // place.
        surface: Surface | null,
        method: string,
        path: string,
        body?: unknown,
        idempotencyKey?: string,
    ): Promise<T> {
        const headers: Record<string, string> = {
            Accept: 'application/json',
        };

        const bearer = token();
        if (bearer !== null) {
            headers.Authorization = `Bearer ${bearer}`;
        }

        if (body !== undefined) {
            headers['Content-Type'] = 'application/json';
        }

        // Every write carries a key. The server dedupes; the caller may supply
        // its own (the rider app reuses the UUIDv7 from its action queue, so a
        // replayed action is recognised as the same action).
        if (WRITE_METHODS.has(method)) {
            headers['Idempotency-Key'] = idempotencyKey ?? crypto.randomUUID();
        }

        const init: RequestInit = { method, headers };

        if (body !== undefined) {
            init.body = JSON.stringify(body);
        }

        const surfaceSegment = surface === null ? '' : `/${surface}`;

        // The one opt-in that flips ops off the live merchant set entirely
        // (docs/decisions/0012-testing-merchant-isolation.md, backend repo).
        // Appended here rather than in every ops.ts hook so nothing can
        // forget it. Never sent unless it's explicitly 'testing' — the
        // backend already treats an absent flag as live, so this is belt and
        // braces, not the only thing keeping test data out of live views.
        const withDataMode =
            surface === 'ops' && dataMode?.() === 'testing'
                ? `${path}${path.includes('?') ? '&' : '?'}data_mode=testing`
                : path;

        const response = await fetch(`${baseUrl}/api${surfaceSegment}${withDataMode}`, init);

        if (response.status === 204) {
            return undefined as T;
        }

        const payload: unknown = await response.json();

        if (!response.ok) {
            const { error } = payload as ApiErrorBody;

            throw new ApiError(
                response.status,
                error?.code ?? 'unknown',
                error?.message ?? response.statusText,
                error?.details,
            );
        }

        return payload as T;
    }

    return {
        get: <T>(surface: Surface, path: string) => request<T>(surface, 'GET', path),
        post: <T>(surface: Surface, path: string, body: unknown, idempotencyKey?: string) =>
            request<T>(surface, 'POST', path, body, idempotencyKey),
        patch: <T>(surface: Surface, path: string, body: unknown, idempotencyKey?: string) =>
            request<T>(surface, 'PATCH', path, body, idempotencyKey),
        delete: <T>(surface: Surface, path: string, idempotencyKey?: string) =>
            request<T>(surface, 'DELETE', path, undefined, idempotencyKey),
        // The unified login door: no surface prefix, because the caller
        // doesn't have one yet. The response says which surface it got.
        login: <T>(path: string, body: unknown) => request<T>(null, 'POST', path, body),
    };
}

export type ApiClient = ReturnType<typeof createClient>;
